// Code from: https://reactflow.dev/examples/edges/floating-edges
import dagre from 'dagre';
import { useCallback, useContext, useEffect, useRef } from 'react';
import {
  MarkerType,
  Position,
  Edge as ReactFlowEdge,
  Node as ReactFlowNode,
  useReactFlow,
} from 'reactflow';

import {
  FIXED_NODE_MASS,
  FORCE_STRENGTH,
  GENERIC_NODE_MASS,
  MAX_RESOLVE_INTERSECTIONS_ITERATIONS,
  NODE_INTERSECTION_PADDING,
} from '@/Pages/Graph/src/components/debug/graph/constants';
import {
  NodePosition,
  useGraphStore,
} from '@/Pages/Graph/src/components/debug/graph/store';
import { getCollapsedGraph } from '@/Pages/Graph/src/components/debug/graph/subgraph.utils';
import { Graph } from '@/Pages/Graph/src/data/misc';
import { StudioFeatureFlagsContext } from '@/Pages/Graph/src/hooks/useStudioFeatureFlagsContext';

export const hasNoPosition = (node: ReactFlowNode) =>
  node.position.x === 0 && node.position.y === 0;

// react flow node positions are the top left corner of the node
// but dagre node positions are the center of the node, so this
const convertDagreNodePositiontoReactFlowNodePosition = (node: dagre.Node) => {
  return {
    x: node.x - node.width / 2,
    y: node.y - node.height / 2,
  };
};

export const getSelfLinkedPath = (sourceNode: ReactFlowNode) => {
  const position = sourceNode.positionAbsolute;
  const width = sourceNode.width;
  const height = sourceNode.height;

  if (!position || !width || !height)
    return ['', 0, 0] as [string, number, number];

  const startX = position.x + width;
  const startY = position.y + height - 6;

  const endX = position.x + width;
  const endY = position.y + 6;

  const labelX = position.x + width + 30;
  const labelY = position.y + height / 2;

  const path = [
    `M ${startX} ${startY}`,
    `C ${startX + 40} ${startY + 30} ${endX + 40} ${endY - 30} ${endX} ${endY}`,
  ].join(' ');

  return [path, labelX, labelY] as [string, number, number];
};

export const getBidirectionPath = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
}: {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
}) => {
  const centerX = (sourceX + targetX) / 2;
  const centerY = (sourceY + targetY) / 2;

  const diffX = targetX - sourceX;
  const diffY = targetY - sourceY;

  const sgn = Math.sign(diffX) === Math.sign(diffY) ? -1 : 1;

  const offsetX = centerX - (sgn * diffY) / 2;
  const offsetY = centerY + (sgn * diffX) / 2;

  const labelX = 0.25 * sourceX + 0.5 * offsetX + 0.25 * targetX;
  const labelY = 0.25 * sourceY + 0.5 * offsetY + 0.25 * targetY;

  return [
    `M ${sourceX} ${sourceY} Q ${offsetX} ${offsetY} ${targetX} ${targetY}`,
    labelX,
    labelY,
  ] as [string, number, number];
};

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
export function getEdgeParams(source: ReactFlowNode, target: ReactFlowNode) {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
  const targetPos = getEdgePosition(target, targetIntersectionPoint);

  return {
    sx: sourceIntersectionPoint.x,
    sy: sourceIntersectionPoint.y,
    tx: targetIntersectionPoint.x,
    ty: targetIntersectionPoint.y,
    sourcePos,
    targetPos,
  };
}

// returns the position (top,right,bottom or right) passed node compared to the intersection point
function getEdgePosition(
  node: ReactFlowNode,
  intersectionPoint: { x: number; y: number }
) {
  const n = { ...node.positionAbsolute, ...node };

  if (
    typeof n.x !== 'number' ||
    typeof n.y !== 'number' ||
    typeof n.width !== 'number' ||
    typeof n.height !== 'number'
  ) {
    return Position.Top;
  }

  const nx = Math.round(n.x);
  const ny = Math.round(n.y);
  const px = Math.round(intersectionPoint.x);
  const py = Math.round(intersectionPoint.y);

  if (px <= nx + 1) {
    return Position.Left;
  }
  if (px >= nx + n.width - 1) {
    return Position.Right;
  }
  if (py <= ny + 1) {
    return Position.Top;
  }
  if (py >= n.y + n.height - 1) {
    return Position.Bottom;
  }

  return Position.Top;
}

// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
function getNodeIntersection(
  intersectionNode: ReactFlowNode,
  targetNode: ReactFlowNode
) {
  // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a
  const {
    width: intersectionNodeWidth,
    height: intersectionNodeHeight,
    positionAbsolute: intersectionNodePosition,
  } = intersectionNode;

  const targetPosition = targetNode.positionAbsolute;

  if (
    typeof intersectionNodeWidth !== 'number' ||
    typeof intersectionNodeHeight !== 'number' ||
    intersectionNodePosition == null ||
    targetPosition == null ||
    typeof targetNode.width !== 'number' ||
    typeof targetNode.height !== 'number'
  ) {
    return { x: 0, y: 0 };
  }

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x + targetNode.width / 2;
  const y1 = targetPosition.y + targetNode.height / 2;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);

  const a = 1 / (Math.abs(xx1) + Math.abs(yy1) + 1e-32);
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

// Intersection resolution system for preventing node overlaps
interface NodeBounds {
  id: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  isFixed: boolean; // true if position was saved/manual
  parent?: string;
}

function calculateNodeBounds(
  nodes: ReactFlowNode[],
  savedPositions: Record<string, NodePosition>
): NodeBounds[] {
  return nodes.map((node) => {
    const width = (node.style?.width as number) || 150;
    const height = (node.style?.height as number) || 32;
    const left = node.position.x;
    const top = node.position.y;

    return {
      id: node.id,
      left,
      top,
      right: left + width,
      bottom: top + height,
      width,
      height,
      isFixed: Boolean(savedPositions[node.id]?.isFixed),
      parent: node.parentId,
    };
  });
}

function findIntersections(
  nodeBounds: NodeBounds[]
): [NodeBounds, NodeBounds][] {
  const conflicts: [NodeBounds, NodeBounds][] = [];

  for (let i = 0; i < nodeBounds.length; i++) {
    for (let j = i + 1; j < nodeBounds.length; j++) {
      const nodeA = nodeBounds[i];
      const nodeB = nodeBounds[j];

      // Skip if nodes are in different parent contexts (subgraphs)
      if (nodeA.parent !== nodeB.parent) continue;

      // Use consistent padding for detection and separation
      const padding = NODE_INTERSECTION_PADDING;

      // Check for actual intersection or too-close proximity
      const horizontalOverlap =
        nodeA.right + padding > nodeB.left &&
        nodeA.left < nodeB.right + padding;
      const verticalOverlap =
        nodeA.bottom + padding > nodeB.top &&
        nodeA.top < nodeB.bottom + padding;

      if (horizontalOverlap && verticalOverlap) {
        conflicts.push([nodeA, nodeB]);
      }
    }
  }

  return conflicts;
}

function calculateSeparationForce(
  nodeA: NodeBounds,
  nodeB: NodeBounds,
  minSeparation: number
): { x: number; y: number } {
  const centerAX = nodeA.left + nodeA.width / 2;
  const centerAY = nodeA.top + nodeA.height / 2;
  const centerBX = nodeB.left + nodeB.width / 2;
  const centerBY = nodeB.top + nodeB.height / 2;

  let dx = centerBX - centerAX;
  let dy = centerBY - centerAY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  dx = dx / distance;
  dy = dy / distance;

  // Calculate actual overlap in both X and Y directions
  const xOverlap = Math.max(
    0,
    (nodeA.width + nodeB.width) / 2 +
      minSeparation -
      Math.abs(centerBX - centerAX)
  );
  const yOverlap = Math.max(
    0,
    (nodeA.height + nodeB.height) / 2 +
      minSeparation -
      Math.abs(centerBY - centerAY)
  );

  // Use the maximum overlap to determine force strength
  const maxOverlap = Math.max(xOverlap, yOverlap);

  // Stronger force that decreases as overlap decreases
  const forceStrength = maxOverlap * FORCE_STRENGTH;

  return {
    x: dx * forceStrength,
    y: dy * forceStrength,
  };
}

function applyDisplacements(
  nodes: ReactFlowNode[],
  displacements: Record<string, { dx: number; dy: number }>,
  dampening: number
): ReactFlowNode[] {
  return nodes.map((node) => {
    const displacement = displacements[node.id];
    if (!displacement) return node;

    return {
      ...node,
      position: {
        x: node.position.x + displacement.dx * dampening,
        y: node.position.y + displacement.dy * dampening,
      },
    };
  });
}

function resolveIntersections(
  nodes: ReactFlowNode[],
  savedPositions: Record<string, NodePosition>
): ReactFlowNode[] {
  const minSeparation = NODE_INTERSECTION_PADDING;
  let currentNodes = [...nodes];
  let previousConflictCount = Infinity;

  for (let iter = 0; iter < MAX_RESOLVE_INTERSECTIONS_ITERATIONS; iter++) {
    const nodeBounds = calculateNodeBounds(currentNodes, savedPositions);
    const conflicts = findIntersections(nodeBounds);

    if (conflicts.length === 0) break;
    // If we're not making progress, break early
    if (conflicts.length >= previousConflictCount && iter > 3) {
      break;
    }
    previousConflictCount = conflicts.length;

    // Apply separation forces
    const displacements: Record<string, { dx: number; dy: number }> = {};

    for (const [nodeA, nodeB] of conflicts) {
      const force = calculateSeparationForce(nodeA, nodeB, minSeparation);

      // Distribute force based on node "mass" (fixed nodes resist more)
      const massA = nodeA.isFixed ? FIXED_NODE_MASS : GENERIC_NODE_MASS;
      const massB = nodeB.isFixed ? FIXED_NODE_MASS : GENERIC_NODE_MASS;
      const totalMass = massA + massB;

      // Apply force away from each other
      displacements[nodeA.id] = {
        dx: (displacements[nodeA.id]?.dx || 0) - force.x * (massB / totalMass),
        dy: (displacements[nodeA.id]?.dy || 0) - force.y * (massB / totalMass),
      };

      displacements[nodeB.id] = {
        dx: (displacements[nodeB.id]?.dx || 0) + force.x * (massA / totalMass),
        dy: (displacements[nodeB.id]?.dy || 0) + force.y * (massA / totalMass),
      };
    }

    // Higher dampening in early iterations, then reduce for fine-tuning
    const iterationDampening = iter < 3 ? 0.8 : 0.4;
    currentNodes = applyDisplacements(
      currentNodes,
      displacements,
      iterationDampening
    );
  }

  return currentNodes;
}

export const layoutGraph = ({
  nodes,
  edges,
  subgraphs,
  needsInitialLayout,
  needsNodePositions,
  savedPositions,
  showGraphZoomControls,
}: {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
  subgraphs: string[];
  needsInitialLayout: boolean;
  needsNodePositions: (nodeId: string) => boolean;
  savedPositions: Record<string, NodePosition>;
  showGraphZoomControls: boolean;
}) => {
  const g = new dagre.graphlib.Graph({ compound: true });

  const newNodes = [...nodes];
  const newEdges = [...edges];

  g.setGraph({ rankdir: 'TB', ranker: 'tight-tree' });
  g.setDefaultEdgeLabel(() => ({}));

  // set node sizes and parents
  newNodes.forEach((node) => {
    if (subgraphs.includes(node.id)) {
      g.setNode(node.id, { height: 32 });
    } else {
      g.setNode(node.id, {
        width: Math.max(0, node.data.label.length * 7 + 64),
        height: 32,
      });
    }
    if (node.parentId) g.setParent(node.id, node.parentId);
  });

  newEdges.forEach((edge) => {
    g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  // set node positions
  const nodesWithPositions = newNodes.map((node) => {
    const nodeWithPosition = g.node(node.id);
    let { x: nodeX, y: nodeY } =
      convertDagreNodePositiontoReactFlowNodePosition(nodeWithPosition);

    const parent = node.parentId ? g.node(node.parentId) : null;
    if (parent) {
      const { x: parentX, y: parentY } =
        convertDagreNodePositiontoReactFlowNodePosition(parent);
      nodeX -= parentX;
      nodeY -= parentY;
    }
    if (hasNoPosition(node) || needsNodePositions(node.id)) {
      node.position = { x: nodeX, y: nodeY };
    } else {
      node.position = { x: node.position.x, y: node.position.y };
    }

    node.style = {
      ...node.style,
      width: nodeWithPosition.width,
      height: nodeWithPosition.height,
    };

    return node;
  });

  // Resolve any intersections caused by fixed node positions (only if feature flag is enabled)
  const resolvedNodes =
    needsInitialLayout && showGraphZoomControls
      ? resolveIntersections(nodesWithPositions, savedPositions)
      : nodesWithPositions;

  return { nodes: resolvedNodes, edges: newEdges };
};

// Helper hook to ensure fitView is called after DOM measurements are complete
export const useDeferredFitView = () => {
  const { fitView } = useReactFlow();

  const deferredFitView = useCallback(
    (options?: { padding?: number; duration?: number }) => {
      // Use multiple animation frames to ensure ReactFlow has completed its internal rendering and measurements
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Add a small timeout as additional safety for complex layouts
          setTimeout(() => {
            fitView(options);
          }, 10);
        });
      });
    },
    [fitView]
  );

  return deferredFitView;
};

export const useInitializeGraph = ({
  graphData,
  subgraphSelection,
  setNodes,
  setEdges,
  needsInitialLayoutRef,
  needsInitialZoomRef,
}: {
  graphData: Graph | undefined;
  subgraphSelection: Set<string>;
  setNodes: (
    nodes: ReactFlowNode[] | ((nodes: ReactFlowNode[]) => ReactFlowNode[])
  ) => void;
  setEdges: (
    edges: ReactFlowEdge[] | ((edges: ReactFlowEdge[]) => ReactFlowEdge[])
  ) => void;
  needsInitialLayoutRef?: React.MutableRefObject<boolean>;
  needsInitialZoomRef?: React.MutableRefObject<boolean>;
}) => {
  const { showGraphZoomControls } = useContext(StudioFeatureFlagsContext);
  const setInitialZoom = useSetInitialZoom();
  const nodePositions = useGraphStore((state) => state.nodePositions);
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);
  const setNeedsFitting = useGraphStore((state) => state.setNeedsFitting);
  const needsFitting = useGraphStore((state) => state.needsFitting);
  const deferredFitView = useDeferredFitView();

  useEffect(() => {
    if (needsInitialLayoutRef) {
      needsInitialLayoutRef.current = true;
      if (fitToViewMode) {
        setNeedsFitting(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subgraphSelection]);

  // Process graph data to create nodes and edges
  useEffect(() => {
    if (!graphData) return;
    const collapsed = getCollapsedGraph(
      graphData,
      Array.from(subgraphSelection)
    );

    setNodes([]);
    setEdges([]);

    const animationFrameId = window.requestAnimationFrame(() => {
      // now, we convert graph nodes into react-flow objects
      const flowNodes: ReactFlowNode[] = [];
      // let flowEdges: ReactFlowEdge[] = [];
      const presentSubgraphs = new Set<string>();
      for (const node of collapsed.nodes) {
        const segments = node.id.split(':');
        const parentIds = segments.slice(0, -1);
        const id = segments.at(-1)!;

        const subgraphId = parentIds.join(':');

        const isInSubgraph = (id: string) =>
          collapsed.subgraphs.includes(id) && subgraphSelection.has(id);

        let label = node.id;
        if (isInSubgraph(subgraphId)) {
          presentSubgraphs.add(subgraphId);

          // Add all ancestor subgraphs as well
          for (let i = 1; i < parentIds.length; i++) {
            const ancestorSubgraphId = parentIds.slice(0, i).join(':');
            if (isInSubgraph(ancestorSubgraphId)) {
              presentSubgraphs.add(ancestorSubgraphId);
            }
          }

          label = id;
        }

        flowNodes.push({
          id: node.id,
          position: { x: 0, y: 0 },
          data: {
            label,
            meta: node.data,
            isSubgraph: collapsed.subgraphs.includes(node.id),
          },
          parentId: isInSubgraph(subgraphId) ? subgraphId : undefined,
          type: 'node',
          deletable: false,
          extent: isInSubgraph(subgraphId) ? 'parent' : undefined,
        });
      }

      const maxDepth = Math.max(
        ...collapsed.subgraphs.map((i) => i.split(':').length)
      );

      for (const subgraph of collapsed.subgraphs) {
        if (!presentSubgraphs.has(subgraph)) continue;
        const segments = subgraph.split(':');
        const parentId = segments.slice(0, -1).join(':') || undefined;

        flowNodes.unshift({
          id: segments.join(':'),
          parentId,
          data: { label: segments.at(-1) || subgraph },
          position: { x: 0, y: 0 },
          type: 'subgraph',
          style: {
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
            zIndex: -1 - maxDepth + segments.length,
          },
          deletable: false,
          extent: parentId ? 'parent' : undefined,
        });
      }

      // Set edges immediately after nodes
      const flowEdges = collapsed.edges.map((edge) => ({
        type: 'edge',
        // we need to replace spaces with dashes to avoid issues with react-flow
        id: `${edge.source.split(' ').join('-')}-${edge.target
          .split(' ')
          .join('-')}`,
        source: edge.source,
        target: edge.target,
        sourceHandle: `${edge.source}-source`,
        targetHandle: `${edge.target}-target`,
        data: { meta: edge.data, conditional: edge.conditional },
        deletable: false,
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeDasharray: edge.conditional ? '3 1' : undefined },
      }));

      // Only apply layout to nodes that don't have saved positions
      const nodesToLayout = flowNodes.map((node) => {
        const position = nodePositions[node.id] || node.position;
        return { ...node, position };
      });

      const layout = layoutGraph({
        nodes: nodesToLayout,
        edges: flowEdges,
        subgraphs: collapsed.subgraphs.filter((i) => subgraphSelection.has(i)),
        needsInitialLayout: !!needsInitialLayoutRef?.current,
        needsNodePositions: (nodeId) =>
          Boolean(needsInitialLayoutRef?.current && !nodePositions[nodeId]),
        savedPositions: nodePositions,
        showGraphZoomControls,
      });
      setNodes(
        (nodes) => layout.nodes?.map((n, i) => ({ ...nodes[i], ...n })) ?? []
      );
      setEdges(layout.edges ?? []);
      if (needsInitialLayoutRef) {
        needsInitialLayoutRef.current = false;
      }
      if (needsInitialZoomRef) {
        if (needsInitialZoomRef.current) {
          setInitialZoom();
          needsInitialZoomRef.current = false;
        }
      }
      if (fitToViewMode || needsFitting) {
        deferredFitView({ padding: 0.2, duration: 100 });
      }
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- don't re-render when setInitialZoom changes
  }, [
    graphData,
    setNodes,
    setEdges,
    subgraphSelection,
    needsInitialLayoutRef,
    nodePositions,
  ]);

  const needsLayout = needsInitialLayoutRef?.current;
  useEffect(() => {
    if (needsFitting && !needsLayout) {
      deferredFitView({ padding: 0.2, duration: 100 });
      setNeedsFitting(false);
    }
  }, [needsFitting, deferredFitView, setNeedsFitting, needsLayout]);
};

export const useResizeGraph = (ref: React.RefObject<HTMLDivElement>) => {
  const setInitialZoom = useSetInitialZoom();
  // use a ref so updates to setInitialZoom don't cause us to re-run the effect
  // but we can still use the latest value of setInitialZoom
  const setInitialZoomRef = useRef(setInitialZoom);
  useEffect(() => {
    setInitialZoomRef.current = setInitialZoom;
  }, [setInitialZoom]);

  const container = ref.current;
  useEffect(() => {
    if (container) {
      let timerRef = 0;
      const resizeObserver = new ResizeObserver(() => {
        if (container) {
          window.clearTimeout(timerRef);
          timerRef = window.setTimeout(() => {
            setInitialZoomRef.current(300);
          }, 100);
        }
      });
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }
  }, [container]);
};

export const useSetInitialZoom = () => {
  const { showGraphZoomControls } = useContext(StudioFeatureFlagsContext);
  const { setCenter, getNodes } = useReactFlow();
  const fitToViewMode = useGraphStore((state) => state.fitToViewMode);
  const setNeedsFitting = useGraphStore((state) => state.setNeedsFitting);
  const deferredFitView = useDeferredFitView();

  const setInitialZoom = useCallback(
    (duration?: number) => {
      if (!showGraphZoomControls) {
        deferredFitView({ duration });
        return;
      }
      if (fitToViewMode) {
        setNeedsFitting(true);
        return;
      }
      const nodes = getNodes();
      if (!nodes.length) return;
      let centerX = 0;
      let centerY = 0;
      nodes.forEach((node) => {
        // width and height are set as numbers in layoutNodes
        const width = (node.style?.width ?? 0) as number;
        const height = (node.style?.height ?? 0) as number;

        // center of node
        centerX += node.position.x + width / 2;
        centerY += node.position.y + height / 2;
      });
      centerX /= nodes.length;
      centerY /= nodes.length;
      setCenter(centerX, centerY, {
        zoom: 1,
        duration,
      });
    },
    [
      setCenter,
      getNodes,
      fitToViewMode,
      setNeedsFitting,
      deferredFitView,
      showGraphZoomControls,
    ]
  );
  return setInitialZoom;
};
